import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowsClockwise, UserPlus, ClockCounterClockwise, Tag, UsersThree, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { itApi, ASSET_STATUS_META, OWNERSHIP_META } from './itApi';
import { CategoryChip } from './categoryColor';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildAssetLabel } from '@/components/labels/labelSizes';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import PurchaseSummaryCard from '@/components/PurchaseSummaryCard';
import ConsumableReceiptDialog from './ConsumableReceiptDialog';
import { ItemLifecycleActions } from '@/components/lifecycle/ItemLifecycleActions';
import { formatDate as svcDate, formatDateTime as svcDateTime } from '@/utils/datetime';

const yen = (n) => (typeof n === 'number' ? `¥${n.toLocaleString('ja-JP')}` : '—');
const fmtDate = (s) => (s ? svcDate(s) || '—' : '—');
const fmtDateTime = (s) => (s ? svcDateTime(s) || '—' : '—');

// Action → label + color. Covers permanent assign/return (from AssetDetailDialog assign flow)
// and scanner-emitted actions (lend / return_scanner / verify / move).
const HISTORY_META = {
  assign: { label: '割当', color: '#0052FF' },
  return: { label: '返却', color: '#d97706' },
  lend: { label: '貸出', color: '#7c3aed' },
  return_scanner: { label: '返却', color: '#d97706' },
  verify: { label: '棚卸', color: '#14b8a6' },
  move: { label: '場所移動', color: '#f97316' },
};

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-zinc-200 last:border-0">
    <span className="text-xs text-zinc-500 shrink-0">{label}</span>
    <span className="text-sm text-zinc-800 text-right break-all">{value || '—'}</span>
  </div>
);

export default function AssetDetailDialog({ open, onClose, assetId, employees = [], canManage, onChanged }) {
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [empId, setEmpId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const load = async () => {
    if (!assetId) return;
    try {
      const a = await itApi.asset(assetId);
      setAsset(a);
      if (a.asset_type === 'consumable' && canManage) {
        setReceipts(await itApi.receipts(assetId).catch(() => []));
      } else {
        setReceipts([]);
      }
      if (canManage) {
        const h = await itApi.assetHistory(assetId).catch(() => []);
        setHistory(h);
      }
    } catch {
      toast.error('資産の読み込みに失敗しました');
    }
  };

  useEffect(() => {
    if (open) { setEmpId(''); setNote(''); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assetId]);

  const assign = async () => {
    if (!empId) { toast.error('社員を選択してください'); return; }
    setBusy(true);
    try {
      await itApi.assignAsset(assetId, { employee_id: empId, note: note || null });
      toast.success('割り当てました');
      setEmpId(''); setNote('');
      await load(); onChanged?.();
    } catch { toast.error('割当に失敗しました'); } finally { setBusy(false); }
  };

  const doReturn = async () => {
    setBusy(true);
    try {
      await itApi.returnAsset(assetId, { note: note || null });
      toast.success('返却しました（在庫に戻しました）');
      setNote('');
      await load(); onChanged?.();
    } catch { toast.error('返却に失敗しました'); } finally { setBusy(false); }
  };

  const sm = asset ? (ASSET_STATUS_META[asset.status] || ASSET_STATUS_META.in_stock) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="asset-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {asset?.name || '資産'}
            {sm && <Badge variant="outline" className={sm.cls}>{sm.label}</Badge>}
            {asset && (
              <Button size="sm" variant="outline" className="h-7 px-2 ml-auto mr-4 text-xs" data-testid="asset-qr-label-btn"
                onClick={() => setLabelOpen(true)}>
                <Tag size={14} className="mr-1" /> ラベル印刷
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {asset && (
          <div className="space-y-4">
            {/* §8.5 ひとつのライフサイクル — 全台帳で同じ操作 */}
            <ItemLifecycleActions register="it" itemId={asset.id} onChanged={() => { load(); onChanged?.(); }}
              testId="it-lifecycle" />
            <div className="rounded-lg border border-zinc-200 px-3 py-1">
              <Row label="資産番号" value={asset.asset_tag} />
              {(asset.old_numbers || []).length > 0 && (
                <div className="flex items-center gap-1.5 py-1" data-testid="it-asset-old-numbers">
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">旧番号</span>
                  <span className="font-mono text-[11px] text-zinc-600" data-no-translate>
                    {asset.old_numbers.join(' / ')}
                  </span>
                  <span className="text-[10px] text-zinc-400">（この番号で検索・スキャンしても見つかります）</span>
                </div>
              )}
              {asset.item_id && <Row label="品目マスター" value={<span className="text-emerald-700 font-semibold">連携済み</span>} />}
              {asset.source_request_no && <Row label="発注元" value={`部品受注入力 ${asset.source_request_no}`} />}
              <Row label="カテゴリ" value={asset.category ? <CategoryChip category={asset.category} /> : null} />
              <Row label="メーカー / 型番" value={[asset.maker, asset.model].filter(Boolean).join(' / ')} />
              <Row label="シリアル番号" value={asset.serial || (asset.serial_pending ? (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                  data-testid="asset-detail-serial-pending">製造番号 未入力</Badge>
              ) : null)} />
              <Row label="使用者" value={asset.assigned_to_name || (asset.is_shared ? '（部署共用）' : null)} />
              <Row label="使用部署" value={asset.assigned_to_department_name ? (
                <span className="inline-flex items-center gap-1">
                  {asset.is_shared && (
                    <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-[10px]" data-testid="asset-detail-shared-badge">
                      <UsersThree size={10} weight="bold" className="mr-0.5" /> 共用
                    </Badge>
                  )}
                  {asset.assigned_to_department_name}
                </span>
              ) : null} />
              <Row label="設置場所" value={asset.location} />
              <Row label="IPアドレス" value={asset.ip_address ? `${asset.ip_address}${asset.vlan_name ? `（${asset.vlan_name}）` : ''}` : '—'} />
              <Row label="購入日" value={fmtDate(asset.purchase_date)} />
              <Row label="所有区分" value={asset.ownership && asset.ownership !== 'owned' ? (
                <span className="inline-flex items-center gap-1.5">
                  <Badge variant="outline" className={`text-[10px] ${OWNERSHIP_META[asset.ownership]?.cls || ''}`} data-testid="asset-detail-ownership">
                    {OWNERSHIP_META[asset.ownership]?.label}
                  </Badge>
                  {asset.lease_end_date && <span className="text-xs text-zinc-500">満了: {fmtDate(asset.lease_end_date)}</span>}
                </span>
              ) : '自社所有'} />
              <Row label="仕入先" value={asset.supplier_name || asset.vendor} />
              <Row label="メーカー品番" value={asset.maker_pn} />
              <Row label="注文URL" value={asset.reference_url ? (
                <a href={asset.reference_url} target="_blank" rel="noreferrer"
                  className="text-teal-700 hover:underline break-all" data-testid="asset-detail-order-url">
                  {asset.reference_url}
                </a>
              ) : null} />
              <Row label="保証期限" value={fmtDate(asset.warranty_until)} />
              <Row label="取得価額" value={yen(asset.cost)} />
              <Row label="耐用年数" value={asset.useful_life_effective
                ? `${asset.useful_life_effective} 年${asset.useful_life_source === 'category_default' ? '（カテゴリ既定）' : ''}`
                : <span className="text-amber-700" data-testid="asset-detail-life-unset">耐用年数 未設定</span>} />
              <Row label="現在簿価（定額法・月割り・残存1円）" value={
                <span className="font-semibold text-teal-700">
                  {yen(asset.book_value)}
                  {asset.book_value_status === 'no_life' && (
                    <span className="ml-1.5 text-[11px] font-normal text-amber-700" data-testid="asset-detail-book-note">
                      （耐用年数 未設定のため未償却）
                    </span>
                  )}
                  {asset.book_value_status === 'minor_expense' && (
                    <span className="ml-1.5 text-[11px] font-normal text-zinc-500" data-testid="asset-detail-book-note">
                      （少額のため即時費用化）
                    </span>
                  )}
                </span>} />
              <Row label="減価償却累計" value={yen(asset.accumulated_depreciation)} />
              {asset.notes && <Row label="備考" value={asset.notes} />}
            </div>

            {/* B. IT消耗品 — 入数・最終購入・入庫履歴 */}
            {asset.asset_type === 'consumable' && (
              <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-3" data-testid="asset-detail-consumable">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-purple-800">在庫と購入の履歴</p>
                  {canManage && (
                    <Button size="sm" className="h-8 bg-purple-600 hover:bg-purple-700"
                      onClick={() => setReceiptOpen(true)} data-testid="asset-detail-receipt-btn">
                      <Package size={14} className="mr-1" /> 入庫を記録
                    </Button>
                  )}
                </div>
                <div className="mt-2 rounded-lg border border-zinc-200 bg-white px-3 py-1">
                  <Row label="在庫" value={`${asset.on_hand_qty ?? 0} ${asset.unit || '個'}`} />
                  <Row label="入数" value={asset.pack_label ? (
                    <span data-testid="asset-detail-pack">
                      {asset.pack_label}
                      {asset.pack_price != null && `・パック価格 ${yen(Number(asset.pack_price))}`}
                      {asset.pack_unit_price != null && `（単価 ${yen(asset.pack_unit_price)}）`}
                    </span>
                  ) : null} />
                  <Row label="最終購入日" value={asset.last_purchase_date ? fmtDate(asset.last_purchase_date) : null} />
                  <Row label="最終購入単価" value={asset.last_purchase_price != null ? yen(asset.last_purchase_price) : null} />
                </div>
                {receipts.length > 0 ? (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-[11px]" data-testid="asset-detail-receipts">
                      <thead>
                        <tr className="border-b border-zinc-200 text-left text-zinc-500">
                          <th className="pb-1 pr-2 font-semibold">入庫日</th>
                          <th className="pb-1 pr-2 font-semibold">数量</th>
                          <th className="pb-1 pr-2 font-semibold">単価</th>
                          <th className="pb-1 pr-2 font-semibold">購入先</th>
                          <th className="pb-1 pr-2 font-semibold">REQ-No.</th>
                          <th className="pb-1 pr-2 font-semibold">受領者</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receipts.map((r, i) => (
                          <tr key={r.id} className="border-b border-zinc-100" data-testid={`asset-receipt-row-${i}`}>
                            <td className="py-1 pr-2 whitespace-nowrap">{fmtDate(r.received_date)}</td>
                            <td className="py-1 pr-2 whitespace-nowrap tabular-nums">
                              {r.qty_base} {r.unit || ''}
                              {r.unit_mode === 'pack' && (
                                <span className="ml-1 text-zinc-400">({r.qty_entered}{r.purchase_unit || 'セット'})</span>
                              )}
                            </td>
                            <td className="py-1 pr-2 whitespace-nowrap tabular-nums">{r.unit_price != null ? yen(r.unit_price) : '—'}</td>
                            <td className="py-1 pr-2" data-no-translate>{r.supplier_name || '—'}</td>
                            <td className="py-1 pr-2 font-mono" data-no-translate>{r.source_request_no || '—'}</td>
                            <td className="py-1 pr-2" data-no-translate>{r.received_by || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-2 text-[11px] text-zinc-400" data-testid="asset-detail-receipts-empty">
                    入庫履歴はまだありません
                  </p>
                )}
              </div>
            )}

            {/* 購入実績 — 入荷確認の実績から集計（編集権限者のみ見える） */}
            <PurchaseSummaryCard
              ledger="it" assetId={asset.id}
              orderPrefill={{
                item_id: asset.id, item_source: 'it',
                name: asset.name, part_no: asset.asset_tag,
                maker: asset.maker, maker_pn: asset.maker_pn,
                model_no: asset.model, uom: asset.unit || '個',
                reference_url: asset.reference_url,
              }}
            />

            {canManage && (
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 space-y-3" data-testid="asset-assign-panel">
                <p className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
                  <UserPlus size={15} weight="bold" /> 割当 / 返却
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={empId} onValueChange={setEmpId}>
                    <SelectTrigger data-testid="asset-assign-employee" className="h-10 flex-1"><SelectValue placeholder="社員を選択" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.department_name ? `（${e.department_name}）` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid="asset-assign-submit" disabled={busy} onClick={assign} className="h-10 bg-teal-700 hover:bg-teal-800">
                    <UserPlus size={16} className="mr-1.5" /> 割り当てる
                  </Button>
                </div>
                <Textarea data-testid="asset-assign-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="メモ（任意）" className="min-h-[44px] text-sm" />
                {asset.assigned_to_employee_id && (
                  <Button data-testid="asset-return-submit" disabled={busy} variant="outline" onClick={doReturn} className="w-full h-9 text-amber-700 border-amber-200 hover:bg-amber-50">
                    <ArrowsClockwise size={15} className="mr-1.5" /> 返却（在庫に戻す）
                  </Button>
                )}
              </div>
            )}

            {canManage && (
              <div data-testid="asset-history-list">
                <p className="text-xs font-semibold text-zinc-600 mb-2 flex items-center gap-1.5">
                  <ClockCounterClockwise size={15} weight="bold" /> 履歴（割当・貸出・棚卸・移動）
                </p>
                {history.length === 0 ? (
                  <p className="text-xs text-zinc-400">履歴はありません</p>
                ) : (
                  <ul className="space-y-1.5">
                    {history.map((h) => {
                      const meta = HISTORY_META[h.action] || { label: h.action, color: '#71717a' };
                      return (
                        <li key={h.id} className="flex items-center justify-between gap-2 text-xs border-l-2 pl-2 py-0.5"
                            style={{ borderColor: meta.color }}>
                          <span className="min-w-0">
                            <span className="font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                            {h.employee_name ? <>{' · '}{h.employee_name}</> : null}
                            {h.note ? <span className="text-zinc-400">（{h.note}）</span> : null}
                            {h.source === 'scanner' && <span className="ml-1.5 text-[9px] px-1 rounded bg-cyan-50 text-cyan-700 border border-cyan-200">scan</span>}
                            {h.by_name && <span className="text-zinc-400 ml-1">by {h.by_name}</span>}
                          </span>
                          <span className="text-zinc-400 whitespace-nowrap">{fmtDateTime(h.at)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            <LinkedDocsPanel targetType="it_asset" targetId={asset.id} className="pt-2 border-t border-zinc-200" />
          </div>
        )}
      </DialogContent>
      {asset && (
        <LabelPrintDialog open={labelOpen} onClose={() => setLabelOpen(false)}
          labels={[buildAssetLabel(asset, asset.assigned_to_department_name || '')]}
          title="資産ラベル印刷" />
      )}
      {asset && asset.asset_type === 'consumable' && (
        <ConsumableReceiptDialog open={receiptOpen} asset={asset}
          onClose={() => setReceiptOpen(false)}
          onSaved={() => { load(); onChanged?.(); }} />
      )}
    </Dialog>
  );
}
